/**
 * Duration variant pages (1 / 3 / 5 minute typing tests) — the long-tail SEO
 * surface. Each reuses the test component with a fixed duration. One dynamic
 * `$variant` route renders any entry. The hub is a 60-second test.
 */
import { m } from "@/paraglide/messages";

export const HUB_DURATION_SEC = 60;

export interface TypingFaq {
  q: () => string;
  a: () => string;
}

export interface TypingVariant {
  slug: string;
  path: string;
  durationSec: number;
  navName: () => string;
  metaTitle: () => string;
  metaDescription: () => string;
  h1: () => string;
  intro: () => string;
  howTo: () => string;
  faqs: TypingFaq[];
}

const BASE = "/typing-speed-test";

/** Duration-specific copy — without it every variant page is the same template
 * with only the number swapped (thin/doorway-content pattern). */
interface VariantUnique {
  howTo: () => string;
  extraFaqs: TypingFaq[];
}

function variant(
  slug: string,
  durationSec: number,
  minutes: number,
  unique: VariantUnique,
): TypingVariant {
  // Lazy + locale-aware: the active locale is per-request during SSR, so the
  // label must never be computed at module scope.
  const label = () => m.typing_label_minutes({ n: minutes });
  return {
    slug,
    path: `${BASE}/${slug}`,
    durationSec,
    navName: () => m.typing_variant_nav({ label: label() }),
    metaTitle: () => m.typing_variant_meta_title({ label: label() }),
    metaDescription: () =>
      m.typing_variant_meta_description({ label: label() }),
    h1: () => m.typing_variant_h1({ label: label() }),
    intro: () => m.typing_variant_intro({ label: label() }),
    howTo: unique.howTo,
    faqs: [
      {
        q: () => m.typing_variant_faq1_q({ label: label() }),
        a: () => m.typing_variant_faq1_a({ label: label() }),
      },
      {
        q: () => m.typing_variant_faq2_q(),
        a: () => m.typing_variant_faq2_a(),
      },
      ...unique.extraFaqs,
    ],
  };
}

export const TYPING_VARIANTS: Record<string, TypingVariant> = {
  "1-minute-typing-test": variant("1-minute-typing-test", 60, 1, {
    howTo: () => m.typing_u1_howto(),
    extraFaqs: [
      { q: () => m.typing_u1_faq3_q(), a: () => m.typing_u1_faq3_a() },
      { q: () => m.typing_u1_faq4_q(), a: () => m.typing_u1_faq4_a() },
    ],
  }),
  "3-minute-typing-test": variant("3-minute-typing-test", 180, 3, {
    howTo: () => m.typing_u3_howto(),
    extraFaqs: [
      { q: () => m.typing_u3_faq3_q(), a: () => m.typing_u3_faq3_a() },
      { q: () => m.typing_u3_faq4_q(), a: () => m.typing_u3_faq4_a() },
    ],
  }),
  "5-minute-typing-test": variant("5-minute-typing-test", 300, 5, {
    howTo: () => m.typing_u5_howto(),
    extraFaqs: [
      { q: () => m.typing_u5_faq3_q(), a: () => m.typing_u5_faq3_a() },
      { q: () => m.typing_u5_faq4_q(), a: () => m.typing_u5_faq4_a() },
    ],
  }),
};

export const TYPING_VARIANT_LIST: TypingVariant[] =
  Object.values(TYPING_VARIANTS);

/** Safe lookup (`Object.hasOwn` so inherited keys don't resolve and 500). */
export function getVariant(slug: string): TypingVariant | undefined {
  return Object.hasOwn(TYPING_VARIANTS, slug)
    ? TYPING_VARIANTS[slug]
    : undefined;
}
